import React, { useMemo } from "react";
import {
  NoOrders,
  ArrowLeftMD,
  formatNumber,
  formatDate,
  ArrowLeftLG,
} from "../../normalizer";
import { withOrdersContainer } from "../../../containers/gamification2026";
import EmptyStateLayout from "../../shared/EmptyStateLayout";
import CustomButton from "../../shared/CustomButton";
import CustomDataTable from "../../shared/CustomDataTable";

const getStatusColor = (status) => {
  if (!status) return "processing";
  switch (status.toLowerCase()) {
    case "enviado":
      return "sent";
    case "entregue":
      return "delivered";
    default:
      return "processing";
  }
};

const itemColumns = [
  { key: "index", label: "Nº", totalsLabel: "Total" },
  { key: "name", label: "Artigo" },
  { key: "quantity", label: "Qtd." },
  {
    key: "points",
    label: "Pontos",
    totals: true,
    render: (val) => formatNumber(val),
    // totalsFormatter: (val) => formatNumber(val) + " pontos",
  },
];

const OrderShow = (props) => {
  const { selectedOrder } = props;

  const messageNoOrder = "Por favor escolha uma encomenda válida.";

  if (!selectedOrder.id) {
    return (
      <EmptyStateLayout message={messageNoOrder}>
        <div className="gl-no-store">
          <NoOrders className="mr-3 dead-grey gl-no-store__image" />
        </div>
      </EmptyStateLayout>
    );
  }

  const {
    number,
    checkout_date,
    total_points,
    status,
    items = [],
    shipment_address = {},
    order_payment: orderPayment,
  } = selectedOrder;

  const {
    first_name: firstName,
    last_name: lastName,
    phone,
    email,
    address1,
    city,
    // API can return null (not undefined), so the = {} default won't help —
    // normalise to an object here so country.name is always safe to read.
    country: countryRaw,
    zip_code: zipCode,
    main_name: mainName,
  } = shipment_address;
  const country = countryRaw || {};
  const source = orderPayment && orderPayment.source;
  const provider = orderPayment && orderPayment.provider;

  const statusColor = getStatusColor(status);

  const tableItems = useMemo(
    () =>
      items.map((item, i) => ({
        id: item.id,
        index: i + 1,
        name: item.product ? item.product.name : "",
        quantity: item.quantity,
        points: item.points || 0,
      })),
    [items],
  );

  const renderItemRow = (item, index, className) => (
    <tr key={item.id || index} className={`data-table__row ${className}__row`}>
      <td
        className={`data-table__cell ${className}__cell ${className}__cell--index`}
      >
        {item.index}
      </td>
      <td
        className={`data-table__cell ${className}__cell ${className}__cell--name`}
      >
        {item.name}
      </td>
      <td
        className={`data-table__cell ${className}__cell ${className}__cell--quantity`}
      >
        {item.quantity}
      </td>
      <td
        className={`data-table__cell ${className}__cell ${className}__cell--points`}
      >
        {formatNumber(item.points)}
      </td>
    </tr>
  );

  const sortFn = (data, sort) => {
    const sorted = [...data];
    sorted.sort((a, b) => {
      let valA = a[sort.key];
      let valB = b[sort.key];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();
      if (valA < valB) return sort.direction === "asc" ? -1 : 1;
      if (valA > valB) return sort.direction === "asc" ? 1 : -1;
      return 0;
    });
    return sorted;
  };

  return (
    <div className="order-show">
      <CustomButton
        link
        tight
        to="/profile/orders"
        secondary
        large
        className="order-show__back"
      >
        <ArrowLeftLG className="order-show__back-icon" />
        Voltar
      </CustomButton>

      <div className="order-show__cards">
        <div className="order-show__card">
          <h5 className="order-show__card-title">Detalhes da encomenda</h5>
          <div className="order-show__card-grid">
            <div className="order-show__field">
              <span className="order-show__field-label">Nº encomenda</span>
              <span className="order-show__field-value">{number}</span>
            </div>
            <div className="order-show__field">
              <span className="order-show__field-label">Data encomenda</span>
              <span className="order-show__field-value">
                {formatDate(checkout_date)}
              </span>
            </div>
            <div className="order-show__field">
              <span className="order-show__field-label">Total de pontos</span>
              <span className="order-show__field-value">
                {formatNumber(total_points)} pontos
              </span>
            </div>
            <div className="order-show__field">
              <span className="order-show__field-label">Estado</span>
              <span
                className={`order-show__status order-show__status--${statusColor}`}
              >
                {status}
              </span>
            </div>
          </div>
        </div>

        {orderPayment && source && (
          <div className="order-show__card">
            <h5 className="order-show__card-title">Dados de pagamento</h5>
            <div className="order-show__card-grid">
              <div className="order-show__field">
                <span className="order-show__field-label">Entidade</span>
                <span className="order-show__field-value">
                  {source.entity || "-"}
                </span>
              </div>
              <div className="order-show__field">
                <span className="order-show__field-label">Referência</span>
                <span className="order-show__field-value">
                  {source.reference || "-"}
                </span>
              </div>
              <div className="order-show__field">
                <span className="order-show__field-label">Valor</span>
                <span className="order-show__field-value">
                  {source.value ? `${formatNumber(source.value)} €` : "-"}
                </span>
              </div>
              {provider && provider.validity > 0 && (
                <div className="order-show__field">
                  <span className="order-show__field-label">Validade</span>
                  <span className="order-show__field-value">
                    {provider.validity} dia(s)
                  </span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="order-show__section">
        <h5 className="order-show__section-title">Artigos</h5>
        <CustomDataTable
          className="order-show-items"
          columns={itemColumns}
          data={tableItems}
          renderRow={renderItemRow}
          rowKey={(item) => item.id}
          sortFn={sortFn}
          emptyMessage="Sem artigos nesta encomenda."
        />
      </div>

      <div className="order-show__section">
        <h5 className="order-show__section-title">Morada de entrega</h5>
        <div className="order-show__address">
          <div className="order-show__address-label">{mainName || "Casa"}</div>
          <div className="order-show__address-content">
            <div className="order-show__address-col">
              <span className="order-show__address-text">{address1}</span>
              <span className="order-show__address-text">
                {zipCode} {city}
              </span>
              <span className="order-show__address-text">{country.name}</span>
            </div>
            <div className="order-show__address-col">
              <span className="order-show__address-text">
                {firstName} {lastName}
              </span>
              {email && (
                <span className="order-show__address-text">{email}</span>
              )}
              {phone && (
                <span className="order-show__address-text">{phone}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default withOrdersContainer(OrderShow);
